import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getRaceDetails, updateSession, getRaceProtests } from '../services/api';
import '../styles/fonts.css';
import '../styles/organizer.css';

const SESSION_KEY = (id) => `organizer_auth_${id}`;
const PWD_KEY = (id) => `organizer_pwd_${id}`;

function OrganizerRaceForm() {
  const { id, champId, sessionId } = useParams();
  const { t } = useTranslation();
  const [race, setRace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const [broadcastUrl, setBroadcastUrl] = useState('');
  const [itelemetryUrl, setItelemetryUrl] = useState('');
  const [sortedResults, setSortedResults] = useState([]);
  const [driverPenalties, setDriverPenalties] = useState({});
  const [driverPenaltyReasons, setDriverPenaltyReasons] = useState({});
  const [activeTab, setActiveTab] = useState('penalties');
  const [protests, setProtests] = useState([]);
  const [protestsLoading, setProtestsLoading] = useState(false);

  const isAuth = sessionStorage.getItem(SESSION_KEY(id)) === 'true';
  const password = sessionStorage.getItem(PWD_KEY(id)) || '';

  useEffect(() => {
    getRaceDetails(sessionId)
      .then(data => {
        setRace(data);
        setBroadcastUrl(data.broadcast_url || '');
        setItelemetryUrl(data.itelemetry_url || '');

        const results = (data.results || []).slice().sort(
          (a, b) => a.finish_position - b.finish_position
        );
        setSortedResults(results);
        const penalties = {};
        results.forEach(r => { penalties[r.cust_id] = r.penalty_points || 0; });
        setDriverPenalties(penalties);
        const reasons = {};
        (data.penalties || []).forEach(p => { reasons[p.cust_id] = p.reason || ''; });
        setDriverPenaltyReasons(reasons);
      })
      .catch(() => setRace(null))
      .finally(() => setLoading(false));
  }, [sessionId]);

  if (!isAuth) {
    return (
      <div className="OrganizerPage">
        <Header />
        <main>
          <div className="organizer-content">
            <p className="organizer-error">
              {t('organizer.restrictedAccess')} <Link to={`/leagues/${id}/organizer`}>{t('organizer.loginPanel')}</Link>
            </p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (loading) return (
    <div className="OrganizerPage">
      <Header />
      <main><div className="organizer-content"><p className="organizer-loading">{t('common.loading')}</p></div></main>
      <Footer />
    </div>
  );

  if (!race) return (
    <div className="OrganizerPage">
      <Header />
      <main><div className="organizer-content"><p className="organizer-error">{t('organizer.raceNotFound')}</p></div></main>
      <Footer />
    </div>
  );

  async function handleSubmit(e) {
    if (e) e.preventDefault();
    setError(null);
    setSuccess(false);
    setSaving(true);
    try {
      // Build a map of cust_id → display_name (unique per driver)
      const driverNameMap = {};
      sortedResults.forEach(r => { driverNameMap[r.cust_id] = r.display_name; });
      const driver_penalties = Object.entries(driverPenalties).map(([cust_id, penalty_points]) => ({
        cust_id: parseInt(cust_id),
        penalty_points: parseFloat(penalty_points) || 0,
        reason: driverPenaltyReasons[cust_id] || '',
        display_name: driverNameMap[cust_id] || '',
      }));
      await updateSession(sessionId, password, {
        broadcast_url: broadcastUrl || null,
        itelemetry_url: itelemetryUrl || null,
        result_status: 'confirmed',
        driver_penalties,
      });
      setSuccess(true);
    } catch (err) {
      setError(err?.error || t('organizer.saveErrorData'));
    } finally {
      setSaving(false);
    }
  }

  const trackLabel = [race.track_name, race.config_name].filter(Boolean).join(' — ');
  const hasMultiClass = [...new Set(sortedResults.map(r => r.car_class_name).filter(Boolean))].length > 1;

  // Multi-race: detect by distinct simsession names OR by duplicate cust_ids
  const simsessionNames = [...new Set(sortedResults.map(r => r.simsession_name).filter(Boolean))];
  const uniqueDriverCount = new Set(sortedResults.map(r => r.cust_id)).size;
  const isMultiRace = simsessionNames.length > 1 || sortedResults.length > uniqueDriverCount;

  // Build one entry per driver for multi-race penalty table
  const driverRows = isMultiRace
    ? Object.values(
        sortedResults.reduce((acc, r) => {
          if (!acc[r.cust_id]) {
            acc[r.cust_id] = { cust_id: r.cust_id, display_name: r.display_name, car_class_name: r.car_class_name, heats: {} };
          }
          acc[r.cust_id].heats[r.simsession_name] = r.finish_position + 1;
          return acc;
        }, {})
      ).sort((a, b) => {
        // Sort by last simsession (Feature/main race) position
        const last = simsessionNames[simsessionNames.length - 1];
        return (a.heats[last] || 99) - (b.heats[last] || 99);
      })
    : [];

  return (
    <div className="OrganizerPage">
      <Header />
      <main>
        <div className="organizer-content">

          <div className="organizer-panel-header" style={{ marginBottom: 24 }}>
            <div>
              <p className="organizer-panel-label">
                <Link
                  to={`/leagues/${id}/organizer/championships/${champId}`}
                  className="organizer-back-link"
                >
                  ← {race.championship_name}
                </Link>
              </p>
              <h1 className="organizer-panel-name">{trackLabel}</h1>
              <p className="organizer-champ-meta" style={{ marginTop: 4 }}>
                {race.start_time
                  ? new Date(race.start_time).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                  : ''}
              </p>
            </div>
            <Link to={`/races/${sessionId}`} className="btn-secondary btn-sm" target="_blank" rel="noopener noreferrer">
              {t('organizer.viewPublicResult')}
            </Link>
          </div>

          <div className="organizer-form-card" style={{ paddingTop: 16 }}>
            {/* Tab navigation */}
            <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: 24 }}>
              {[
                { key: 'penalties', label: t('organizer.tabPenalties') },
                { key: 'links',     label: t('organizer.tabLinks') },
                { key: 'protests',  label: t('protest.tabProtests') },
              ].map(tab => (
                <button key={tab.key} type="button"
                  style={{
                    padding: '8px 18px', background: 'none', border: 'none', cursor: 'pointer',
                    color: activeTab === tab.key ? 'var(--accent, #e8c84a)' : 'rgba(255,255,255,0.55)',
                    borderBottom: activeTab === tab.key ? '2px solid var(--accent, #e8c84a)' : '2px solid transparent',
                    fontWeight: activeTab === tab.key ? 600 : 400,
                    fontSize: '0.95rem', marginBottom: -1, transition: 'color 0.15s',
                  }}
                  onClick={() => {
                    setActiveTab(tab.key);
                    setError(null);
                    setSuccess(false);
                    if (tab.key === 'protests' && protests.length === 0) {
                      setProtestsLoading(true);
                      getRaceProtests(sessionId, password)
                        .then(data => setProtests(data))
                        .catch(() => setProtests([]))
                        .finally(() => setProtestsLoading(false));
                    }
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab: Punições */}
            {activeTab === 'penalties' && (
              <div className="organizer-form">
                {sortedResults.length > 0 ? (
                  <div className="form-section">
                    <p className="organizer-subtitle" style={{ marginBottom: 12 }}>
                      {t('organizer.driverPenaltySubtitle')}
                    </p>
                    <div className="penalty-table-scroll">
                      {isMultiRace ? (
                        <table className="points-config-table penalty-drivers-table">
                          <thead>
                            <tr>
                              {hasMultiClass && <th>{t('organizer.class')}</th>}
                              <th className="col-driver">{t('common.driver')}</th>
                              {simsessionNames.map(sn => (
                                <th key={sn} className="col-pos">{sn}</th>
                              ))}
                              <th className="col-penalty-pts">{t('organizer.penaltyPts')}</th>
                              <th className="col-driver">{t('organizer.penaltyReason')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {driverRows.map(d => (
                              <tr key={d.cust_id} className={(driverPenalties[d.cust_id] || 0) > 0 ? 'penalty-row-active' : ''}>
                                {hasMultiClass && <td className="col-class">{d.car_class_name || '--'}</td>}
                                <td className="col-driver">{d.display_name}</td>
                                {simsessionNames.map(sn => (
                                  <td key={sn} className="col-pos">{d.heats[sn] != null ? `${d.heats[sn]}º` : '—'}</td>
                                ))}
                                <td className="col-penalty-pts">
                                  <input type="number" min="0" step="0.5"
                                    className="form-input points-pts-input"
                                    value={driverPenalties[d.cust_id] ?? 0}
                                    onChange={e => setDriverPenalties(prev => ({ ...prev, [d.cust_id]: e.target.value }))}
                                  />
                                </td>
                                <td className="col-driver">
                                  {(driverPenalties[d.cust_id] || 0) > 0 && (
                                    <input type="text" className="form-input"
                                      placeholder={t('organizer.reasonPlaceholder')}
                                      value={driverPenaltyReasons[d.cust_id] || ''}
                                      onChange={e => setDriverPenaltyReasons(prev => ({ ...prev, [d.cust_id]: e.target.value }))}
                                    />
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <table className="points-config-table penalty-drivers-table">
                          <thead>
                            <tr>
                              <th className="col-pos">{t('common.pos')}</th>
                              {hasMultiClass && <th>{t('organizer.class')}</th>}
                              <th className="col-driver">{t('common.driver')}</th>
                              <th className="col-penalty-pts">{t('organizer.penaltyPts')}</th>
                              <th className="col-driver">{t('organizer.penaltyReason')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedResults.map(r => (
                              <tr key={r.cust_id} className={(driverPenalties[r.cust_id] || 0) > 0 ? 'penalty-row-active' : ''}>
                                <td className="col-pos">{r.finish_position + 1}º</td>
                                {hasMultiClass && <td className="col-class">{r.car_class_name || '--'}</td>}
                                <td className="col-driver">{r.display_name}</td>
                                <td className="col-penalty-pts">
                                  <input type="number" min="0" step="0.5"
                                    className="form-input points-pts-input"
                                    value={driverPenalties[r.cust_id] ?? 0}
                                    onChange={e => setDriverPenalties(prev => ({ ...prev, [r.cust_id]: e.target.value }))}
                                  />
                                </td>
                                <td className="col-driver">
                                  {(driverPenalties[r.cust_id] || 0) > 0 && (
                                    <input type="text" className="form-input"
                                      placeholder={t('organizer.reasonPlaceholder')}
                                      value={driverPenaltyReasons[r.cust_id] || ''}
                                      onChange={e => setDriverPenaltyReasons(prev => ({ ...prev, [r.cust_id]: e.target.value }))}
                                    />
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="organizer-subtitle">{t('organizer.noResultsYet')}</p>
                )}
                {error && <p className="form-error" style={{ marginTop: 12 }}>{error}</p>}
                {success && <p className="form-success" style={{ marginTop: 12 }}>{t('organizer.saveSuccess')}</p>}
                <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                  <button type="button" className="btn-primary" disabled={saving} onClick={handleSubmit}>
                    {saving ? t('common.saving') : t('organizer.save')}
                  </button>
                  <Link to={`/leagues/${id}/organizer/championships/${champId}`} className="btn-secondary">
                    {t('organizer.cancel')}
                  </Link>
                </div>
              </div>
            )}

            {/* Tab: Links */}
            {activeTab === 'links' && (
              <div className="organizer-form">
                <div className="form-section">
                  <label className="form-label">
                    {t('organizer.broadcastVideoLink')}
                    <span className="optional">{t('organizer.optional')}</span>
                    <input type="url" value={broadcastUrl}
                      onChange={e => setBroadcastUrl(e.target.value)}
                      placeholder="https://youtube.com/watch?v=..."
                      className="form-input"
                    />
                  </label>
                  <label className="form-label" style={{ marginTop: 12 }}>
                    {t('organizer.itelemetryLink')}
                    <span className="optional">{t('organizer.optional')}</span>
                    <input type="url" value={itelemetryUrl}
                      onChange={e => setItelemetryUrl(e.target.value)}
                      placeholder="https://itelemetry.app/session/..."
                      className="form-input"
                    />
                  </label>
                </div>
                {error && <p className="form-error" style={{ marginTop: 12 }}>{error}</p>}
                {success && <p className="form-success" style={{ marginTop: 12 }}>{t('organizer.saveSuccess')}</p>}
                <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                  <button type="button" className="btn-primary" disabled={saving} onClick={handleSubmit}>
                    {saving ? t('common.saving') : t('organizer.save')}
                  </button>
                  <Link to={`/leagues/${id}/organizer/championships/${champId}`} className="btn-secondary">
                    {t('organizer.cancel')}
                  </Link>
                </div>
              </div>
            )}
            {/* Tab: Protestos */}
            {activeTab === 'protests' && (
              <div className="organizer-form">
                {protestsLoading ? (
                  <p className="organizer-loading">{t('common.loading')}</p>
                ) : protests.length === 0 ? (
                  <p className="organizer-subtitle">{t('protest.noProtests')}</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {protests.map(p => (
                      <div key={p.id} style={{
                        background: 'rgba(255,255,255,0.04)',
                        borderRadius: 8,
                        padding: '14px 18px',
                        borderLeft: '3px solid var(--accent, #e8c84a)',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                          <strong style={{ fontSize: '0.95rem' }}>{p.display_name}</strong>
                          <span style={{
                            fontSize: '0.75rem',
                            padding: '2px 8px',
                            borderRadius: 12,
                            background: p.status === 'accepted' ? '#2f855a' : p.status === 'rejected' ? '#c53030' : '#744210',
                            color: '#fff',
                            whiteSpace: 'nowrap',
                          }}>
                            {p.status === 'accepted' ? t('protest.statusAccepted')
                              : p.status === 'rejected' ? t('protest.statusRejected')
                              : t('protest.statusPending')}
                          </span>
                        </div>
                        <p style={{ fontSize: '0.85rem', opacity: 0.85, margin: '0 0 8px' }}>{p.description}</p>
                        {p.attachment_url && (
                          <a href={p.attachment_url} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: '0.8rem', color: 'var(--accent, #e8c84a)' }}>
                            ↗ {t('protest.attachment')}
                          </a>
                        )}
                        <p style={{ fontSize: '0.75rem', opacity: 0.5, marginTop: 8 }}>
                          {t('protest.submittedAt')}: {new Date(p.created_at).toLocaleString('pt-BR')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}

export default OrganizerRaceForm;
